import { useContext, useState } from "react";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  PHONE_COUNTRIES,
  joinPhoneNumber,
  splitPhoneNumber,
  type PhoneCountry,
} from "@common/logic/phone-countries";
import { CountryCodeContext } from "../country-provider";

/**
 * State for a country-code + national-number phone input whose value is the full
 * international number ("+36701234567"). The national part is kept as typed (so a
 * leading "06" doesn't vanish mid-typing); the emitted value is normalized.
 * Pasting/typing a full "+…" or "00…" number into the national part re-picks the country.
 */
export function usePhoneNumberInput({
  value,
  onChange,
  defaultCountryCode,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Country preselected for an empty value; defaults to the market country (HU/SK). */
  defaultCountryCode?: string;
}) {
  const marketCountry = useContext(CountryCodeContext);
  const [state, setState] = useState(() => ({
    ...splitPhoneNumber(value, defaultCountryCode ?? marketCountry ?? DEFAULT_PHONE_COUNTRY_CODE),
    emitted: value,
  }));

  // The value changed from outside (form reset, prefill): re-derive the parts.
  if (value !== state.emitted) {
    setState({ ...splitPhoneNumber(value, state.country.code), emitted: value });
  }

  const emit = (country: PhoneCountry, national: string) => {
    const next = joinPhoneNumber(country, national);
    setState({ country, national, emitted: next });
    onChange(next);
  };

  return {
    country: state.country,
    national: state.national,
    setCountry: (country: PhoneCountry) => emit(country, state.national),
    setNational: (text: string) => {
      const trimmed = text.trim();
      if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
        const digits = trimmed.slice(trimmed.startsWith("+") ? 1 : 2).replace(/\D/g, "");
        if (!PHONE_COUNTRIES.some((entry) => digits.startsWith(entry.dialCode))) {
          // Calling code still being typed ("+3"): show it raw until it resolves.
          setState({ country: state.country, national: trimmed, emitted: "" });
          onChange("");
          return;
        }
        const parts = splitPhoneNumber(trimmed, state.country.code);
        emit(parts.country, parts.national);
        return;
      }
      emit(state.country, text.replace(/[^\d ]/g, ""));
    },
  };
}
